import { isEqual } from "es-toolkit";
import { first } from "es-toolkit/compat";
import { type Getter, atom } from "jotai";
import { atomFamily, atomWithRefresh } from "jotai/utils";

import { type DocumentMetadata, DocumentMetadataSchema } from "@moment/api-collab/api-types";
import { Logger } from "@moment/logging";

import { $accessTokenFunc } from "../authenication";
import { LOCAL_WORKSPACE_NAME } from "../constants";
import { $docsApi } from "../docsapi";
import { $document, $documentLocalPath } from "../document-list";
import { $remoteWorkspaceName } from "../document/current-document.atom";
import { env } from "../env";
import { fs } from "../filesystem";
import { $getWorkspace } from "../workspaces";
import type { DocumentMetadataSource } from "./document-metadata.types";

const $documentMetadataFromDisk = atomFamily((documentPath: string) => {
	const logger = new Logger("documentMetadataFromDisk");

	const metadataAtom = atom(async (get) => {
		const enabled = !env.isSsr && env.isDesktopApp;

		if (!documentPath) {
			logger.warn("Attempted to fetch document metadata from disk without a path");
			return undefined;
		}

		if (!enabled) {
			return undefined;
		}

		const fsApi = fs();
		const metadataPath = await get(fsApi.pathJoin([documentPath, "moment.yml"]));

		// Read YAML file
		const parsedData = await get(fsApi.readYamlFile({ filePath: metadataPath }));
		const parsed = DocumentMetadataSchema.safeParse(parsedData);
		if (!parsed.success) {
			logger.error("Error parsing metadata from disk", {
				documentPath,
				cause: parsed.error,
			});
			return undefined;
		}

		return parsed.data;
	});
	metadataAtom.debugLabel = `documentMetadata.disk/${documentPath}`;

	return metadataAtom;
});

export const $documentMetadataFromRemote = atomFamily(
	({ workspaceName, documentId }: { workspaceName: string; documentId: string }) => {
		const metadataAtom = atomWithRefresh(async (get) => {
			const { getAccessTokenSilently } = get($accessTokenFunc);
			let token = undefined;
			if (getAccessTokenSilently) {
				token = await getAccessTokenSilently();
			}

			// document might be public. Token can be undefined.
			return await get($docsApi).document.metadata.read({ workspaceName, documentId }, token);
		});
		metadataAtom.debugLabel = `documentMetadata.remote/${workspaceName}/${documentId}`;

		return atom((get) => ({
			metadata: get(metadataAtom),
			refresh: atom(null, async (_get, set) => set(metadataAtom)),
		}));
	}
);

export const $documentMetadata = atomFamily((documentId: string) => {
	const draft = atom(undefined as DocumentMetadata | undefined);
	const reader = atom(async (get): Promise<DocumentMetadata | undefined> => {
		const documentMetadataRecord = await get($documentMetadataRecord(documentId));
		return documentMetadataRecord?.result?.metadata;
	});
	const writer = atom(null, async (get, set, update: Partial<DocumentMetadata>) => {
		const metadata = await get(reader);
		const nextMetadata = metadata ? { ...metadata, ...update } : (update as DocumentMetadata);

		set(draft, nextMetadata);
	});

	return atom(
		async (get) => {
			const draftValue = get(draft);
			if (draftValue !== undefined) {
				return draftValue;
			}

			return await get(reader);
		},
		async (_get, set, update: Partial<DocumentMetadata>) => {
			await set(writer, update);
		}
	);
});

const findRemoteWorkspace = async (
	get: Getter,
	documentId: string,
	workspaceName: string | undefined
) => {
	const document = await get($document(documentId));
	if (!document) {
		return undefined;
	}

	if (workspaceName && workspaceName !== LOCAL_WORKSPACE_NAME) {
		const workspace = await get($getWorkspace(workspaceName));
		if (!workspace) {
			return undefined;
		}
		return document.remote[workspace.id]?.workspace;
	}
	return first(Object.values(document.remote))?.workspace;
};

type RemoteWorkspaceParams = { documentId: string; workspaceName?: string };

/**
 * Get the publish workspace for a document (defaults to personal workspace)
 */
export const $remoteWorkspaceFromDocsApi = atomFamily((params: RemoteWorkspaceParams) => {
	const remoteWorkspaceAtom = atom(async (get) => {
		return await findRemoteWorkspace(get, params.documentId, params.workspaceName);
	});
	remoteWorkspaceAtom.debugLabel = `remoteWorkspace/${params.documentId}`;

	return remoteWorkspaceAtom;
}, isEqual);

export const $documentMetadataRecord = atomFamily((documentId: string) => {
	const metadataAtom = atom(async (get) => {
		const document = await get($document(documentId));

		if (!document) {
			// This is expected during loading, no need to log
			return undefined;
		}

		const localPath = await get($documentLocalPath(documentId));
		const remoteWorkspace = await get($remoteWorkspaceName(documentId));

		//try to load metadata from disk
		if (localPath) {
			const diskMetadata = await get($documentMetadataFromDisk(localPath));

			if (diskMetadata) {
				return {
					source: "disk" as DocumentMetadataSource,
					metadata: diskMetadata,
				};
			}
		}

		//then try to load metadata from remote
		if (remoteWorkspace) {
			const metadata = await get(
				$documentMetadataFromRemote({ workspaceName: remoteWorkspace, documentId })
			).metadata;

			return { source: "remote" as DocumentMetadataSource, metadata: metadata };
		}

		return undefined;
	});
	metadataAtom.debugLabel = `documentMetadata/${documentId}`;

	return atom(async (get) => ({
		result: await get(metadataAtom),
	}));
});
